import useProperty from "hooks/useProperty";

import { WellDetailCardData } from "api/data";

import { EntityKind } from "models/entityKind";

import { splitUnitFromTitle, useIsFieldGroupEnabled } from "../PanelField";
import {
  PERFORMANCE_METRICS,
  PERFORMANCE_PRODUCTS,
  performanceFieldId
} from "../constants";
import {
  PRODUCT_TABLE_COLUMNS,
  ProductCell,
  ProductTableSection,
  SectionLabel,
  Swatch,
  TableFrame,
  TableHeaderCell,
  TableHeaderRow,
  TableRow
} from "../styles";
import { PropertyTableCell } from "./PropertyTableCell";

const PERFORMANCE_GROUP = "Performance";

/**
 * The metric name is shared by every product, so the header carries it once
 * and each cell carries its own unit. The product-qualified name is on the
 * cell's tooltip.
 */
function MetricHeaderCell({
  metricId,
  fallbackLabel
}: {
  metricId: string;
  fallbackLabel: string;
}): JSX.Element {
  const property = useProperty(
    performanceFieldId(PERFORMANCE_PRODUCTS[0].key, metricId),
    false,
    "",
    EntityKind.Well
  );
  const { label } = splitUnitFromTitle(property?.title);
  return <TableHeaderCell>{label || fallbackLabel}</TableHeaderCell>;
}

function MetricCell({
  product,
  metricId,
  wellData
}: {
  product: string;
  metricId: string;
  wellData: Maybe<WellDetailCardData>;
}): JSX.Element {
  const propertyName = performanceFieldId(product, metricId);
  const raw = wellData?.[propertyName];
  const value = typeof raw === "string" && raw !== "" ? raw : null;

  return <PropertyTableCell propertyName={propertyName} value={value} />;
}

export interface PerformanceTableProps {
  wellData: Maybe<WellDetailCardData>;
}

export function PerformanceTable({ wellData }: PerformanceTableProps): JSX.Element {
  const groupEnabled = useIsFieldGroupEnabled(PERFORMANCE_GROUP);
  if (!groupEnabled) return null;

  return (
    <ProductTableSection>
      <SectionLabel>Performance</SectionLabel>
      <TableFrame>
        <TableHeaderRow $columns={PRODUCT_TABLE_COLUMNS}>
          <TableHeaderCell />
          {PERFORMANCE_METRICS.map((metric) => (
            <MetricHeaderCell
              key={metric.id}
              metricId={metric.id}
              fallbackLabel={metric.fallbackLabel}
            />
          ))}
        </TableHeaderRow>
        {PERFORMANCE_PRODUCTS.map((product) => (
          <TableRow key={product.key} $columns={PRODUCT_TABLE_COLUMNS}>
            <ProductCell>
              <Swatch $color={product.swatch} />
              {product.label}
            </ProductCell>
            {PERFORMANCE_METRICS.map((metric) => (
              <MetricCell
                key={metric.id}
                product={product.key}
                metricId={metric.id}
                wellData={wellData}
              />
            ))}
          </TableRow>
        ))}
      </TableFrame>
    </ProductTableSection>
  );
}
